import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCollection, hasContentfulConfig } from '../lib/contentfulClient';
import { useSiteContent } from '../context/SiteContentContext';

/**
 * Lazily fetches a Contentful collection on mount with pagination support.
 * Also pushes loaded items into SiteContentContext for global access.
 *
 * @param {string} collectionKey - Key matching a registered collection in contentfulClient
 * @param {object} [options]
 * @param {number} [options.pageSize=6] - Items per page
 * @param {boolean} [options.immediate=true] - Fetch first page on mount
 * @returns {{ items, isLoading, hasMore, loadMore, total }}
 */
export function useContentfulCollection(collectionKey, { pageSize = 6, immediate = true } = {}) {
  const { updateCollection } = useSiteContent();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const skipRef = useRef(0);
  const hasMore = items.length < total;

  const load = useCallback(async (skip = 0, append = false) => {
    if (!hasContentfulConfig()) return;
    setIsLoading(true);
    try {
      const result = await fetchCollection(collectionKey, { skip, limit: pageSize });
      setItems((prev) => {
        const next = append ? [...prev, ...result.items] : result.items;
        updateCollection(collectionKey, next);
        return next;
      });
      setTotal(result.total);
      skipRef.current = skip + result.items.length;
    } catch (err) {
      console.error(`[useContentfulCollection] Failed to load "${collectionKey}":`, err);
    } finally {
      setIsLoading(false);
    }
  }, [collectionKey, pageSize, updateCollection]);

  useEffect(() => {
    if (immediate) {
      load(0, false);
    }
  }, [load, immediate]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      load(skipRef.current, true);
    }
  }, [isLoading, hasMore, load]);

  return { items, isLoading, hasMore, loadMore, total };
}
